/**
 * HxConnectionQuality Component
 *
 * A signal-bar network quality indicator showing call connection strength (0–4 bars). Typical use: overlay on a video
 * tile or in a call header.
 *
 * @example
 *   ```html
 *   <!-- 3 bars, medium size -->
 *   <hx-connection-quality [quality]="3" />
 *
 *   <!-- With label, large -->
 *   <hx-connection-quality [quality]="2" size="lg" [showLabel]="true" />
 *   ```;
 *
 * @package @complexcare/harmony
 */

import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';

export type ConnectionQualityLevel = 0 | 1 | 2 | 3 | 4;
export type ConnectionQualitySize = 'xs' | 'sm' | 'md' | 'lg';

/** Maps quality level to label and semantic color */
const QUALITY_META: Record<ConnectionQualityLevel, { label: string; context: string }> = {
  0: { label: 'No signal', context: 'disconnected' },
  1: { label: 'Poor', context: 'poor' },
  2: { label: 'Fair', context: 'fair' },
  3: { label: 'Good', context: 'good' },
  4: { label: 'Excellent', context: 'excellent' },
};

@Component({
  selector: 'hx-connection-quality',
  standalone: true,
  templateUrl: './connection-quality.html',
  styleUrls: ['./connection-quality.css'],
  host: {
    class: 'hx-connection-quality',
    '[class]': 'hostClasses()',
    role: 'img',
    '[attr.aria-label]': 'ariaLabel()',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxConnectionQuality {
  /** Signal quality level: 0 (none) → 4 (excellent) */
  readonly quality = input<ConnectionQualityLevel>(4);

  /** Whether to show text label beneath the bars */
  readonly showLabel = input<boolean>(false);

  /** Size variant */
  readonly size = input<ConnectionQualitySize>('md');

  /** Bar heights as proportions (bar 1 = shortest) */
  protected readonly bars = [1, 2, 3, 4] as const;

  protected readonly meta = computed(() => QUALITY_META[this.quality()]);

  protected readonly ariaLabel = computed(() => `Connection quality: ${this.meta().label}`);

  protected readonly hostClasses = computed(() => ({
    [`hx-connection-quality--${this.size()}`]: true,
    [`hx-connection-quality--${this.meta().context}`]: true,
  }));

  /** Whether bar at position (1-based) should be lit */
  protected isBarActive(bar: number): boolean {
    return bar <= this.quality();
  }
}
